import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import {
  ArrowLeft,
  Check,
  X,
  RefreshCw,
  Plus,
  Users,
  Inbox,
  Trophy,
  ChevronRight,
  Activity,
  Eye,
  Bug,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { generateLeagueCode, validateLeagueCode, LEAGUE_CODE_FORMAT_HINT } from "@/lib/league";
import {
  approvePendingSignup,
  denyPendingSignup,
  approveMembership,
  denyMembership,
  createLeague,
  getAdminDashboard,
  getAdminAuditLog,
  verifyIsAdmin,
  type AdminAuditLogEntry,
} from "@/lib/admin.functions";
import { listTournamentFormats } from "@/lib/tournament-formats.functions";
import {
  formatLabel,
  sortFormatsForDisplay,
  type TournamentFormat,
} from "@/lib/tournament-formats";
import { AdminActivityRow } from "@/components/AdminActivityRow";
import { PendingGoblinBadge } from "@/components/PendingGoblinPanel";
import { GoblinCreditBanner } from "@/components/GoblinCreditBanner";
import { DenyMembershipDialog } from "@/components/DenyMembershipDialog";
import { GoblinRecapModeCard } from "@/components/GoblinRecapModeCard";
import { GoblinRecapHealthCard } from "@/components/GoblinRecapHealthCard";
import { getBrowserTimezone, composeZonedISO, COMMON_TIMEZONES } from "@/lib/timezones";
import { validateLockDates } from "@/lib/lock-date-validation";

export const Route = createFileRoute("/_app/admin/")({
  component: AdminPage,
  head: () => ({ meta: [{ title: "Admin — Bracket Challenge" }] }),
});

type DashboardData = Awaited<ReturnType<typeof getAdminDashboard>>;

function AdminPage() {
  const { session, loading } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [formats, setFormats] = useState<TournamentFormat[]>([]);
  const [recentActivity, setRecentActivity] = useState<AdminAuditLogEntry[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // Server-verified admin status — null = checking, true/false = checked.
  // We never trust the client-side isAdmin flag for gating UI.
  const [serverIsAdmin, setServerIsAdmin] = useState<boolean | null>(null);

  const verifyAdminFn = useAuthedServerFn(verifyIsAdmin);
  const dashboardFn = useAuthedServerFn(getAdminDashboard);
  const auditLogFn = useAuthedServerFn(getAdminAuditLog);
  const formatsFn = useAuthedServerFn(listTournamentFormats);
  const approveSignupFn = useAuthedServerFn(approvePendingSignup);
  const denySignupFn = useAuthedServerFn(denyPendingSignup);
  const approveMembershipFn = useAuthedServerFn(approveMembership);
  const denyMembershipFn = useAuthedServerFn(denyMembership);
  const createLeagueFn = useAuthedServerFn(createLeague);

  const extractMessage = async (e: unknown, fallback: string): Promise<string> => {
    if (e instanceof Error) return e.message;
    if (e instanceof Response) {
      try {
        const text = await e.text();
        return text || `${fallback} (${e.status})`;
      } catch {
        return `${fallback} (${e.status})`;
      }
    }
    if (typeof e === "string") return e;
    return fallback;
  };

  const load = async () => {
    setRefreshing(true);
    try {
      const [dash, activity, fmts] = await Promise.all([
        dashboardFn(),
        auditLogFn({ data: { limit: 5, offset: 0 } }),
        formatsFn(),
      ]);
      setData(dash);
      setRecentActivity(activity.entries);
      setFormats(fmts);
    } catch (e: unknown) {
      const msg = await extractMessage(e, "Failed to load dashboard");
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  // Verify admin status server-side, then load dashboard.
  useEffect(() => {
    if (!session) {
      setServerIsAdmin(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await verifyAdminFn();
        if (cancelled) return;
        setServerIsAdmin(res.isAdmin);
        if (res.isAdmin) await load();
      } catch {
        if (!cancelled) setServerIsAdmin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  if (loading || (session && serverIsAdmin === null)) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }

  if (!serverIsAdmin) {
    return (
      <main className="max-w-md mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">
          Admins only
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          You don't have access to this page.
        </p>
        <Button asChild variant="outline">
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </Button>
      </main>
    );
  }

  const wrap = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast.success(msg);
      await load();
    } catch (e: unknown) {
      const m = await extractMessage(e, "Something went wrong");
      toast.error(m);
    }
  };

  const stats = data?.stats;
  const totalPending =
    (stats?.pendingSignupsCount ?? 0) + (stats?.pendingMembershipsCount ?? 0);

  return (
    <main className="max-w-5xl mx-auto px-4 py-8">
      <GoblinCreditBanner />
      <header className="mb-6 flex items-center justify-between gap-3">
        <div>
          <Link
            to="/home"
            search={{ as: "member" }}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Member view
          </Link>
          <h1 className="font-display text-3xl font-bold text-foreground">
            Admin dashboard
          </h1>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={refreshing}
          >
            <RefreshCw
              className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
            />{" "}
            Refresh
          </Button>
        </div>
      </header>

      {/* Stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatCard
          icon={<Trophy className="w-4 h-4 text-primary" />}
          label="Leagues"
          value={stats?.leagueCount ?? 0}
        />
        <Link to="/admin/members" className="block">
          <StatCard
            icon={<Users className="w-4 h-4 text-primary" />}
            label="Members"
            value={stats?.memberCount ?? 0}
            clickable
          />
        </Link>
        <StatCard
          icon={<Inbox className="w-4 h-4 text-primary" />}
          label="Pending requests"
          value={totalPending}
          accent={totalPending > 0}
        />
        <StatCard
          icon={<Plus className="w-4 h-4 text-primary" />}
          label="New signups"
          value={stats?.pendingSignupsCount ?? 0}
          accent={(stats?.pendingSignupsCount ?? 0) > 0}
        />
      </section>

      <GoblinRecapModeCard />

      <GoblinRecapHealthCard />

      {/* League Formats — shared rules/teams/results across all leagues of that type */}
      <section className="mb-8">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                League Formats
              </h2>
              <span className="text-xs text-muted-foreground">
                ({formats.filter((f) => f.status === "available").length} available
                {formats.length > 0 && ` · ${formats.length} total`})
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            <p className="text-xs text-muted-foreground mb-3">
              Reusable tournament templates that admins can spin up leagues from.
              Teams, seeding, and results live here and apply to every league using
              the format.
            </p>
            <div className="space-y-2">
              {formats.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card p-4 text-sm text-muted-foreground">
                  No formats configured yet.
                </div>
              ) : (
                sortFormatsForDisplay(formats).map((f) => {
                  const isAvailable = f.status === "available";
                  const inner = (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-display text-base font-semibold text-foreground">
                            {f.display_name}
                          </span>
                          {f.status === "available" && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                              Available
                            </span>
                          )}
                          {f.status === "coming_soon" && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400">
                              Coming soon
                            </span>
                          )}
                          {f.status === "archived" && (
                            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              Archived
                            </span>
                          )}
                        </div>
                        {f.description && (
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {f.description}
                          </div>
                        )}
                      </div>
                      {isAvailable && (
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary shrink-0 transition-colors" />
                      )}
                    </div>
                  );
                  return isAvailable ? (
                    <Link
                      key={f.id}
                      to="/admin/tournaments/$tournamentType"
                      params={{ tournamentType: f.code }}
                      className="block rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div
                      key={f.id}
                      className="block rounded-xl border border-dashed border-border bg-card/60 p-4 opacity-80"
                    >
                      {inner}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </details>
      </section>

      {/* Pending requests inbox (combined) */}
      <section className="mb-8">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Inbox className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Pending requests
              </h2>
              <span className="text-xs text-muted-foreground font-mono">
                ({totalPending})
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
          </summary>
          <div className="px-4 pb-4">
            {totalPending === 0 ? (
              <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                All caught up — no pending requests.
              </div>
            ) : (
              <div className="space-y-2">
                {/* New signups */}
                {data?.pendingSignups.map((s) => {
                  const lg = s.league;
                  return (
                    <div
                      key={`ps-${s.id}`}
                      className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary font-semibold">
                            New signup
                          </span>
                          <div className="font-semibold text-foreground truncate">
                            {s.first_name} {s.last_name}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {s.email} →{" "}
                          <span className="text-primary">{lg?.name ?? "—"}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() =>
                            wrap(
                              () =>
                                approveSignupFn({
                                  data: { pendingSignupId: s.id },
                                }),
                              "Approved & invite sent",
                            )
                          }
                        >
                          <Check className="w-4 h-4" /> Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            wrap(
                              () =>
                                denySignupFn({
                                  data: { pendingSignupId: s.id },
                                }),
                              "Denied",
                            )
                          }
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}

                {/* Existing-user join requests */}
                {data?.pendingMemberships.map((m) => {
                  const lg = m.league;
                  const p = m.profile;
                  const attempts = (m as { attempt_count?: number }).attempt_count ?? 1;
                  const priorReason = (m as { denial_reason?: string | null }).denial_reason ?? null;
                  const label = `${p?.first_name ?? ""} ${p?.last_name ?? ""}`.trim() || (p?.email ?? "this user");
                  return (
                    <div
                      key={`mem-${m.id}`}
                      className="rounded-xl border border-border bg-card p-4 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-semibold">
                            Existing user
                          </span>
                          <div className="font-semibold text-foreground truncate">
                            {p?.first_name} {p?.last_name}
                          </div>
                          {attempts > 1 && (
                            <span
                              title={priorReason ? `Prior denial reason: "${priorReason}"` : "Previously denied (no reason given)"}
                              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-semibold"
                            >
                              Attempt #{attempts}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {p?.email} → <span className="text-primary">{lg?.name ?? "—"}</span>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm"
                          onClick={() =>
                            wrap(
                              () =>
                                approveMembershipFn({
                                  data: { membershipId: m.id },
                                }),
                              "Approved",
                            )
                          }
                        >
                          <Check className="w-4 h-4" /> Approve
                        </Button>
                        <DenyMembershipDialog
                          userLabel={label}
                          attemptCount={attempts}
                          priorReason={priorReason}
                          onConfirm={(reason) =>
                            wrap(
                              () =>
                                denyMembershipFn({
                                  data: { membershipId: m.id, reason },
                                }),
                              "Denied",
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </details>
      </section>

      {/* Leagues */}
      <section className="mb-8">
        <details
          className="group rounded-xl border border-border bg-card"
          open={showCreate || undefined}
        >
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Leagues
              </h2>
              {data && (
                <span className="text-xs text-muted-foreground font-mono">
                  ({data.leagues.length})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span onClick={(e) => e.stopPropagation()}>
                <Button asChild variant="outline" size="sm">
                  <Link to="/admin/members">
                    <Users className="w-4 h-4" /> All members
                  </Link>
                </Button>
              </span>
              {!showCreate && (
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCreate(true);
                  }}
                >
                  <Plus className="w-4 h-4" /> New league
                </Button>
              )}
              <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </div>
          </summary>
          <div className="px-4 pb-4">
            {showCreate && (
              <CreateLeagueForm
                formats={formats}
                onCancel={() => setShowCreate(false)}
                onCreated={async (input) => {
                  await wrap(
                    () => createLeagueFn({ data: input }),
                    "League created",
                  );
                  setShowCreate(false);
                }}
              />
            )}

            {data && data.leagues.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-8 text-center mt-3">
                <div className="text-sm text-muted-foreground mb-3">
                  No leagues yet. Create the first one to get started.
                </div>
                {!showCreate && (
                  <Button size="sm" onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4" /> Create league
                  </Button>
                )}
              </div>
            ) : (
              <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="px-4 py-2.5 font-semibold">Name</th>
                      <th className="px-4 py-2.5 font-semibold">Format</th>
                      <th className="px-4 py-2.5 font-semibold">Code</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Members</th>
                      <th className="px-4 py-2.5 font-semibold text-right">Pending</th>
                      <th className="px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {data?.leagues.map((l) => (
                      <tr
                        key={l.id}
                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-foreground flex items-center gap-2 flex-wrap">
                            {l.name}
                            {l.is_hidden && (
                              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                Hidden
                              </span>
                            )}
                            {(l.max_entries_per_member ?? 1) > 1 && (
                              <span
                                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary font-semibold"
                                title={`Members can create up to ${l.max_entries_per_member} entries`}
                              >
                                Multi-entry · {l.max_entries_per_member} max
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{l.slug}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {formatLabel(l.tournament_type, formats)}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-primary text-xs">
                            {l.code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {l.memberCount}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {l.pendingCount > 0 ? (
                            <span className="text-primary font-semibold">
                              {l.pendingCount}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-3 justify-end">
                            <Link
                              to="/$league"
                              params={{ league: l.slug }}
                              className="inline-flex items-center gap-1 text-muted-foreground text-xs hover:text-foreground"
                              title="Open league"
                            >
                              <Eye className="w-3.5 h-3.5" /> Open
                            </Link>
                            <Link
                              to="/admin/leagues/$leagueId"
                              params={{ leagueId: l.id }}
                              className="inline-flex items-center gap-1 text-primary text-xs hover:underline"
                            >
                              Manage <ChevronRight className="w-3 h-3" />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </details>
      </section>

      {/* Recent admin activity */}
      <section className="mt-8">
        <details className="group rounded-xl border border-border bg-card">
          <summary className="flex cursor-pointer items-center justify-between gap-2 px-4 py-3 list-none [&::-webkit-details-marker]:hidden">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              <h2 className="font-display text-lg font-semibold text-foreground">
                Recent admin activity
              </h2>
              {recentActivity.length > 0 && (
                <span className="text-xs text-muted-foreground font-mono">
                  ({recentActivity.length})
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span onClick={(e) => e.stopPropagation()}>
                <Link
                  to="/admin/activity"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  View all <PendingGoblinBadge /> <ChevronRight className="w-3 h-3" />
                </Link>
              </span>
              <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </div>
          </summary>
          <div className="px-4 pb-4">
            {recentActivity.length === 0 ? (
              <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
                No admin actions logged yet.
              </div>
            ) : (
              <div className="space-y-2">
                {recentActivity.map((e) => (
                  <AdminActivityRow key={e.id} entry={e} />
                ))}
              </div>
            )}
          </div>
        </details>
      </section>

      {/* Cross-league insights */}
      <section className="mt-8">
        <Link
          to="/admin/cross-league-report"
          className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-primary" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Cross-league insights
            </h2>
            <span className="text-xs text-muted-foreground">
              Group-stage picks across every league · printable
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      </section>

      {/* Bug reports quick link */}
      <section className="mt-8">
        <Link
          to="/admin/bugs"
          className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card px-4 py-3 hover:border-primary/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-primary" />
            <h2 className="font-display text-lg font-semibold text-foreground">
              Bug reports
            </h2>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </Link>
      </section>

    </main>
  );
}

function StatCard({
  icon,
  label,
  value,
  accent,
  clickable,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  accent?: boolean;
  clickable?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border bg-card p-4 ${
        accent ? "border-primary/40" : "border-border"
      } ${clickable ? "hover:border-primary/40 hover:bg-primary/5 transition-colors cursor-pointer" : ""}`}
    >
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1 font-display text-3xl font-bold tabular-nums ${
          accent ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function CreateLeagueForm({
  onCreated,
  onCancel,
  formats,
}: {
  onCreated: (input: {
    slug: string;
    name: string;
    code: string;
    tournamentType: "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout";
    bonusStatScope: "knockout_only" | "full_tournament";
    maxEntriesPerMember: number;
    addCreatorAsMember: boolean;
    autoApproveJoinByCode: boolean;
    bracketLockAt: string;
    groupStageLockAt: string | null;
    bracketLockTimezone: string;
  }) => Promise<void>;
  onCancel: () => void;
  formats: TournamentFormat[];
}) {
  const activeFormats = formats
    .filter((f) => f.status === "available")
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [code, setCode] = useState(() => generateLeagueCode());
  const codeValidation = validateLeagueCode(code);
  const [tournamentType, setTournamentType] = useState<string>(
    activeFormats[0]?.code ?? "fifa_wc_mens_knockout",
  );
  const [maxEntries, setMaxEntries] = useState<number>(1);
  const [addMeAsMember, setAddMeAsMember] = useState<boolean>(true);
  const [autoApprove, setAutoApprove] = useState<boolean>(false);
  const [timezone, setTimezone] = useState<string>(() => getBrowserTimezone());
  // datetime-local string ("YYYY-MM-DDTHH:mm") in the picked timezone.
  const [bracketLockLocal, setBracketLockLocal] = useState<string>("");
  const [groupLockLocal, setGroupLockLocal] = useState<string>("");
  const isGK = tournamentType === "fifa_wc_mens_group_knockout";

  // Live validation preview — re-uses the same validator the server runs.
  const composeFromLocal = (local: string): string | null => {
    if (!local) return null;
    const [d, t] = local.split("T");
    if (!d || !t) return null;
    const [yy, mm, dd] = d.split("-").map((v) => parseInt(v, 10));
    if (Number.isNaN(yy) || Number.isNaN(mm) || Number.isNaN(dd)) return null;
    return composeZonedISO(new Date(yy, mm - 1, dd), t, timezone);
  };
  const bracketIso = composeFromLocal(bracketLockLocal);
  const groupIso = isGK ? composeFromLocal(groupLockLocal) : null;
  const liveCheck = validateLockDates({
    bracketLockAt: bracketIso,
    groupStageLockAt: groupIso,
    bracketLockAtChanged: true,
    groupStageLockAtChanged: true,
    leagueType: tournamentType as "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout",
    isCreate: true,
  });

  return (
    <form
      className="rounded-xl border border-border bg-card p-4 space-y-3"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!codeValidation.ok) {
          toast.error(codeValidation.error);
          return;
        }
        if (!liveCheck.ok) {
          toast.error(liveCheck.error);
          return;
        }
        await onCreated({
          slug: slug.trim().toLowerCase(),
          name: name.trim(),
          code: code.trim().toUpperCase(),
          tournamentType: tournamentType as "fifa_wc_mens_knockout" | "fifa_wc_mens_group_knockout",
          bonusStatScope: "knockout_only",
          maxEntriesPerMember: Math.max(1, Math.min(20, Math.floor(maxEntries) || 1)),
          addCreatorAsMember: addMeAsMember,
          autoApproveJoinByCode: autoApprove,
          bracketLockAt: bracketIso!,
          groupStageLockAt: groupIso,
          bracketLockTimezone: timezone,
        });
      }}
    >
      <div>
        <Label>League Format</Label>
        {activeFormats.length <= 1 ? (
          <div className="mt-1 inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
            <span className="font-medium">
              {formatLabel(tournamentType, formats)}
            </span>
            {activeFormats.length <= 1 && (
              <span className="text-xs text-muted-foreground">
                (more formats coming soon)
              </span>
            )}
          </div>
        ) : (
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={tournamentType}
            onChange={(e) => setTournamentType(e.target.value)}
          >
            {activeFormats.map((f) => (
              <option key={f.code} value={f.code}>
                {f.display_name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="flex items-baseline justify-between">
            <Label>Name</Label>
            <span className={`text-xs tabular-nums ${name.length > 60 ? "text-destructive" : "text-muted-foreground"}`}>
              {name.length}/60
            </span>
          </div>
          <Input
            required
            maxLength={60}
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              if (!slugEdited)
                setSlug(
                  v
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                    .slice(0, 60),
                );
            }}
            placeholder="Family 2026"
          />
          <p className="mt-1 text-xs text-muted-foreground">Up to 60 characters.</p>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <Label>Slug</Label>
            <span className={`text-xs tabular-nums ${slug.length > 60 ? "text-destructive" : "text-muted-foreground"}`}>
              {slug.length}/60
            </span>
          </div>
          <Input
            required
            maxLength={60}
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
            }}
            placeholder="family-2026"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Used in the league URL. Lowercase letters, numbers, and hyphens, up to 60 characters.
          </p>
        </div>
      </div>
      <div>
        <Label>Code</Label>
        <div className="flex gap-2">
          <Input
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="font-mono"
            aria-invalid={!codeValidation.ok}
            maxLength={32}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCode(generateLeagueCode(name))}
            title="Generate a new code"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
        {!codeValidation.ok ? (
          <p className="mt-1 text-xs text-destructive">{codeValidation.error}</p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">{LEAGUE_CODE_FORMAT_HINT}</p>
        )}
      </div>
      <div>
        <Label>Max entries per member</Label>
        <Input
          type="number"
          min={1}
          max={20}
          step={1}
          value={maxEntries}
          onChange={(e) => setMaxEntries(parseInt(e.target.value, 10) || 1)}
          className="w-32"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          How many separate brackets each member can submit. Default 1; raise this for leagues where members can enter brackets on behalf of kids, partners, etc.
        </p>
      </div>

      {/* Lock dates — required at create. Validation matches updateLeague. */}
      <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3">
        <div className="text-xs font-semibold text-foreground">
          Lock dates {isGK ? "(group + knockout — required)" : "(required)"}
        </div>
        <div>
          <Label className="text-xs">Timezone</Label>
          <select
            className="mt-1 block w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
          >
            {COMMON_TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label} ({tz.value})
              </option>
            ))}
          </select>
        </div>
        {isGK && (
          <div>
            <Label className="text-xs">Group picks lock at</Label>
            <Input
              type="datetime-local"
              required
              value={groupLockLocal}
              onChange={(e) => setGroupLockLocal(e.target.value)}
            />
          </div>
        )}
        <div>
          <Label className="text-xs">
            {isGK ? "Knockout picks lock at" : "Bracket locks at"}
          </Label>
          <Input
            type="datetime-local"
            required
            value={bracketLockLocal}
            onChange={(e) => setBracketLockLocal(e.target.value)}
          />
        </div>
        {!liveCheck.ok && (bracketLockLocal || groupLockLocal) && (
          <p className="text-xs text-destructive">{liveCheck.error}</p>
        )}
        {isGK && (
          <p className="text-xs text-muted-foreground">
            Knockout lock must be at least 1 hour after group lock.
          </p>
        )}
      </div>

      <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
        <Checkbox
          checked={addMeAsMember}
          onCheckedChange={(c) => setAddMeAsMember(c === true)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Add me as a member</span>
          <span className="block text-xs text-muted-foreground">
            On by default. Uncheck to create an empty league — you can join it later from the league's admin page.
          </span>
        </span>
      </label>
      <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
        <Checkbox
          checked={autoApprove}
          onCheckedChange={(c) => setAutoApprove(c === true)}
          className="mt-0.5"
        />
        <span>
          <span className="font-medium">Auto-approve join requests</span>
          <span className="block text-xs text-muted-foreground">
            Off by default. When on, anyone with the league code is approved instantly — no admin review needed. You can toggle this any time from Manage League.
          </span>
        </span>
      </label>
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!codeValidation.ok || !liveCheck.ok}>
          Create league
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
